import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useGetStatsQuery } from '@platform/state/api';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { openClosedOf } from '@platform/core/lib/recordCounts';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { useAppDispatch } from '@platform/state/hooks';
import { dialogOpened } from '@platform/state/slices/uiSlice';
import { NewRecordDialog } from '../form/NewRecordDialog';
import { useCapability } from '../list/toolbar/useCapability';
import { PUBLISHED_STATUS } from './customMenuHeads';
import type { ICustomMenuHeadProps } from './ICustomMenuHeadProps';
import { mainRecordTypeOf } from './moduleSections';
import './menuHeader.css';

/**
 * Заголовок меню модуля з власним меню (прототип v4, Tasks і Knowledge Base): «МОДУЛЬ», назва модуля, «N відкритих · M усього» (база
 * знань — «N оприлюднено · M усього») головної таблиці й основна кнопка «+ Нове завдання» / «+ Нова стаття» на всю ширину — відкриває діалог
 * створення запису. Сторінка списку головної таблиці має власний діалог; на інших сторінках модуля діалог рендерить заголовок (у body).
 */
export function CustomMenuHead({ ws, moduleKey, title, head }: ICustomMenuHeadProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useWorkspaceParams();
  const { data: module } = useModuleDefinition(moduleKey);
  const main = module ? mainRecordTypeOf(module) : undefined;
  const { data: stats } = useGetStatsQuery({ ws, rt: main?.key ?? '', by: 'status', params: {} }, { skip: !main });
  const total = stats?.total ?? 0;
  const counted = head.counted === 'published'
    ? stats?.buckets.find((b) => b.value === PUBLISHED_STATUS)?.count ?? 0
    : openClosedOf(stats, main?.statuses ?? { list: [] }).open;
  const meta = !stats ? ' ' : head.counted === 'published' ? t('modBody.menu.publishedOf', { count: counted, total }) : t('modBody.menu.openOf', { count: counted, total });
  const onOwnList = Boolean(main) && params.rt === main?.key && !params.id;
  // створювати може лише той, хто має можливість edit (гість, спостерігач — без кнопки), як решта рушія
  const canCreate = useCapability(ws, 'edit');
  return (
    <>
      <div className="mnav__head">
        <span className="mnav__eyebrow">{t('modBody.menu.eyebrow')}</span>
        <span className="mnav__title">{title}</span>
        <span className="mnav__meta">{meta}</span>
      </div>
      {canCreate && (
        <button type="button" className="mnav__new" disabled={!main} onClick={() => main && dispatch(dialogOpened({ kind: 'newRecord', recordType: main.key }))}>
          {t(head.newLabelKey)}
        </button>
      )}
      {canCreate && module && main && !onOwnList && createPortal(<NewRecordDialog ws={ws} module={module} rt={main} />, document.body)}
    </>
  );
}
